/**
 * CORE-BN — Front-of-House Attention Capacity · presentation helpers
 *
 * Pure display logic, kept out of the component so it can be tested
 * without rendering and reused by any future surface (Dayboard,
 * OptiBoard, a nav pill) without those surfaces reinventing the
 * vocabulary and drifting from it.
 *
 * The constitutional constraint on presentation is unusually specific:
 *
 *   "A number without its contributing claims must not be shown, because
 *    an unexplained saturation signal will be read as a statement about
 *    whoever is on the desk."
 *
 * So `describeFohBasis` and `fohClaimBasisLabel` are not decoration.
 * Anything rendering the state is obliged to render these alongside it.
 *
 * Language rule: every string produced here has "front of house" or "the
 * shop" as its subject. None takes a person as its subject, and none may.
 */

import {
  FOH_SATURATION_LABELS,
  type FohAttentionBasis,
  type FohAttentionCapacityResult,
  type FohClaimBasis,
  type FohEstimateConfidence,
  type FohInsufficientBasisReason,
  type FohSaturationState,
} from './types';

/** Visual weight of a saturation state. Maps to the shop-wide tone vocabulary. */
export type FohSaturationTone = 'neutral' | 'calm' | 'watch' | 'warn' | 'critical';

export function fohSaturationTone(state: FohSaturationState): FohSaturationTone {
  switch (state) {
    case 'comfortable':
      return 'calm';
    case 'loaded':
      return 'watch';
    case 'saturated':
      return 'warn';
    case 'over_capacity':
      return 'critical';
    default:
      return 'neutral';
  }
}

export function fohSaturationLabel(state: FohSaturationState): string {
  return FOH_SATURATION_LABELS[state] ?? 'Unknown';
}

/** Explains what a claim's count actually rests on. Never omitted from a surface. */
export function fohClaimBasisLabel(basis: FohClaimBasis): string {
  switch (basis) {
    case 'live':
      return 'Counted now';
    case 'inferred':
      return 'Expected from the schedule';
    case 'trailing_rate':
      return 'Estimated from the previous window';
    case 'unobserved':
      return 'Not captured by this shop';
    case 'unavailable':
      return 'Could not be read';
    default:
      return 'Unknown basis';
  }
}

export function fohConfidenceLabel(confidence: FohEstimateConfidence): string {
  switch (confidence) {
    case 'high':
      return 'Good basis';
    case 'moderate':
      return 'Partial basis';
    case 'low':
      return 'Thin basis';
    default:
      return 'No basis';
  }
}

/** Plain-language reason the model declined to produce a band. */
export function fohInsufficientBasisLabel(
  reason: FohInsufficientBasisReason,
): string {
  switch (reason) {
    case 'no_foh_staffing':
      return 'No front-of-house staffing could be established for this window.';
    case 'no_foh_staff_on_duty':
      return 'Nobody is currently clocked in to a front-of-house role.';
    case 'invalid_window':
      return 'The window being estimated is not valid.';
    case 'no_observed_claims':
      return 'None of the front-of-house workload sources could be read.';
    case 'shop_mismatch':
      return 'The observations did not belong to this shop.';
    default:
      return 'The estimate has no basis.';
  }
}

/**
 * The staffing sentence.
 *
 * Says how many people and how the model knows, because a headcount from
 * the roster and a headcount from the time clock are different claims
 * about the world and the reader is entitled to know which one they are
 * being shown.
 */
export function describeFohStaffing(basis: FohAttentionBasis): string {
  const n = basis.fohHeadcount;
  const people = n === 1 ? '1 person' : `${n} people`;
  switch (basis.staffingBasis) {
    case 'clocked_in':
      return n === 0
        ? 'Nobody is clocked in to a front-of-house role'
        : `${people} clocked in to front-of-house roles`;
    case 'roster':
      return basis.timeClockStatus === 'unavailable'
        ? `${people} on the front-of-house roster (the time clock could not be read)`
        : `${people} on the front-of-house roster (this shop does not use the time clock)`;
    case 'shop_override':
      return `${people}, as configured by the shop`;
    default:
      return 'Front-of-house staffing is unknown';
  }
}

/**
 * Everything qualifying the estimate, as a list of short sentences.
 *
 * Returned as a list rather than a paragraph so a surface can render
 * them as footnotes without re-parsing prose.
 */
export function describeFohBasis(result: FohAttentionCapacityResult): string[] {
  const notes: string[] = [];
  const b = result.basis;

  notes.push(describeFohStaffing(b));
  notes.push(
    `${fohConfidenceLabel(b.confidence)} — ${Math.round(b.observedClaimCoverage * 100)}% of the front-of-house claim types contributed.`,
  );

  if (b.extrapolatedClaimKinds.length > 0) {
    notes.push(
      'Call, message and decision volume are estimated from the previous window, not counted in this one.',
    );
  }
  if (b.inferredClaimKinds.length > 0) {
    notes.push(
      'The shop records no arrival event, so expected arrivals — and any waiting customer whose vehicle is not yet at the shop — come from the appointment book rather than from something observed.',
    );
  }
  if (b.unobservedClaimKinds.length > 0) {
    notes.push(
      'Some demands on the front desk are not captured by this shop and are therefore not included — the real load may be higher than shown.',
    );
  }
  if (b.unavailableClaimKinds.length > 0 || b.unavailableSources.length > 0) {
    notes.push(
      'One or more sources could not be read this time, so this estimate is incomplete.',
    );
  }
  if (result.appliedPolicy.usingAllDefaults) {
    notes.push(
      'Using OptiBay default weights — this shop has not tuned its front-of-house load policy.',
    );
  }

  return notes;
}

/**
 * Utilization as a percentage for a progress bar, clamped to 0..100 for
 * display only.
 *
 * The clamp is presentational: a shop at 140% must still READ as 140%,
 * so callers show `formatFohUtilization` for the number and use this
 * only for bar width.
 */
export function fohUtilizationBarPercent(
  result: FohAttentionCapacityResult,
): number {
  if (result.utilization === null) return 0;
  return Math.max(0, Math.min(100, Math.round(result.utilization * 100)));
}

export function formatFohUtilization(result: FohAttentionCapacityResult): string {
  if (result.utilization === null) return '—';
  return `${Math.round(result.utilization * 100)}%`;
}

/** Minutes, rounded for humans. Never implies sub-minute precision. */
export function formatAttentionMinutes(minutes: number | null): string {
  if (minutes === null || !Number.isFinite(minutes)) return '—';
  const rounded = Math.round(minutes);
  return `${rounded} min`;
}

/**
 * Guard used by the surface and pinned by test.
 *
 * A result is only showable as a state when it carries the claims that
 * produced it. This is the constitution's "a number without its
 * contributing claims must not be shown" expressed as a predicate, so
 * that a future refactor which drops the claim list makes the banner
 * disappear rather than silently showing an unexplained verdict.
 */
export function canShowFohSaturationState(
  result: FohAttentionCapacityResult | null,
): boolean {
  if (!result) return false;
  if (result.state === 'unknown') return false;
  return result.contributingClaims.some((c) => c.contributed);
}
